import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import {
  ApiSynchronizerExportResponse, ApiSynchronizerImportResponse, ApiSynchronizerService, SynchronizerHistoryEntityDetails,
  SynchronizerHistoryExportEntityDetails, SynchronizerHistoryImportEntityDetails, SynchronizerHistoryItem
} from '../../../../shared/services/api-synchronizer.service';
import { Environments } from '../../../../shared/models/environments.type';
import { SyncEntities } from '../../../../shared/models/sync-entities.type';

interface SyncResultDetails {
  direction: 'Export' | 'Import';
  batchId: string;
  organizationId: string;
  source: string;
  destination: string;
  securityHashCode: string;
  generatedOn: string;
}

type SyncResult =
  | { success: true; details: SyncResultDetails }
  | { success: false; error: string };

type EntityItemStatus = 'complete' | 'failed' | 'skipped' | 'in-progress';

interface SyncDirection {
  source: Environments;
  destination: Environments;
}

@Component({
  selector: 'app-synchronizer',
  templateUrl: './synchronizer.component.html',
  styleUrls: ['./synchronizer.component.css'],
  imports: [CommonModule, FormsModule, BreadcrumbComponent]
})
export class SynchronizerComponent implements OnInit {
  // Sync only ever flows one hop down the environment chain — dev to qa, or qa to eu.
  readonly directionOptions: SyncDirection[] = [
    { source: 'dev', destination: 'qa' },
    { source: 'qa', destination: 'eu' }
  ];
  readonly entityOptions: SyncEntities[] = [
    'Full', 'Organization', 'DMD', 'Translations', 'TranslationsConfiguration',
    'Surveys', 'Alerts', 'DistributionList', 'StorageConfiguration', 'Settings', 'Notifications'
  ];
  readonly excludeEntityOptions: SyncEntities[] = this.entityOptions.filter(e => e !== 'Full');

  organizationId = '';
  sourceEnvironment: Environments = this.directionOptions[0].source;
  destinationEnvironment: Environments = this.directionOptions[0].destination;
  entities: SyncEntities[] = [];
  entitiesToExclude: SyncEntities[] = [];
  includeGlobalConfigurations = false;

  requesting = signal(false);
  result = signal<SyncResult | null>(null);

  loadingHistory = signal(false);
  historyError = signal<string | null>(null);
  historyItems = signal<SynchronizerHistoryItem[]>([]);
  totalCount = signal<number>(0);
  currentPage = signal<number>(1);
  historyPerPage: number = 10;
  readonly perPageOptions = [5, 10, 25, 50];
  importingBatchId = signal<string | null>(null);

  get totalPages(): number {
    return Math.ceil(this.totalCount() / this.historyPerPage) || 1;
  }

  constructor(
    public organizationStore: OrganizationStoreService,
    private synchronizerService: ApiSynchronizerService) {}

  ngOnInit() {
    this.organizationId = this.organizationStore.selectedOrganizationId() ?? '';
    this.loadHistory();
  }

  isDirectionSelected(direction: SyncDirection): boolean {
    return this.sourceEnvironment === direction.source && this.destinationEnvironment === direction.destination;
  }

  selectDirection(direction: SyncDirection): void {
    this.sourceEnvironment = direction.source;
    this.destinationEnvironment = direction.destination;
  }

  canRun(): boolean {
    return !!this.organizationId &&
      this.sourceEnvironment !== this.destinationEnvironment &&
      this.entities.length > 0;
  }

  formatEntity(entity: SyncEntities): string {
    return entity.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  }

  isFullSelected(): boolean {
    return this.entities.includes('Full');
  }

  isIncluded(entity: SyncEntities): boolean {
    return this.isFullSelected() || this.entities.includes(entity);
  }

  isExcluded(entity: SyncEntities): boolean {
    return this.entitiesToExclude.includes(entity);
  }

  toggleIncludeEntity(entity: SyncEntities): void {
    if (entity === 'Full') {
      // Selecting Full supersedes any individually-picked entities; deselecting it also clears
      // the exclude list, since exclusion only makes sense as a carve-out from a Full sync.
      if (this.isFullSelected()) {
        this.entities = [];
        this.entitiesToExclude = [];
      } else {
        this.entities = ['Full'];
      }
      return;
    }

    // Individual entities are implied/disabled while Full is selected — nothing to toggle.
    if (this.isFullSelected()) return;

    const index = this.entities.indexOf(entity);
    if (index === -1) this.entities.push(entity);
    else this.entities.splice(index, 1);
  }

  toggleExcludeEntity(entity: SyncEntities): void {
    if (!this.isFullSelected()) return;

    const index = this.entitiesToExclude.indexOf(entity);
    if (index === -1) this.entitiesToExclude.push(entity);
    else this.entitiesToExclude.splice(index, 1);
  }

  requestSync(): void {
    if (!this.canRun()) return;

    this.requesting.set(true);
    this.synchronizerService.syncExport(
      this.organizationId,
      this.sourceEnvironment,
      this.destinationEnvironment,
      this.entities,
      this.entitiesToExclude,
      this.includeGlobalConfigurations
    ).subscribe({
      next: (response) => {
        this.requesting.set(false);
        this.result.set({ success: true, details: this.toResultDetails(response) });
        this.currentPage.set(1);
        this.loadHistory();
      },
      error: (err) => {
        this.requesting.set(false);
        this.result.set({ success: false, error: err?.error?.message ?? err?.message ?? 'Synchronization request failed.' });
      }
    });
  }

  private toResultDetails(response: ApiSynchronizerExportResponse | ApiSynchronizerImportResponse): SyncResultDetails {
    if (response.direction === 'Export') {
      return {
        direction: 'Export',
        batchId: response.batchId,
        organizationId: response.organizationId,
        source: response.sourceEnvironment,
        destination: response.destinationEnvironment,
        securityHashCode: response.securityHashCode,
        generatedOn: response.generatedOn
      };
    }
    return {
      direction: 'Import',
      batchId: response.batchId,
      organizationId: response.organizationId,
      source: response.source,
      destination: response.destination,
      securityHashCode: response.hashSecurityCode,
      generatedOn: response.generatedOn
    };
  }

  closeResult(): void {
    this.result.set(null);
  }

  // An entity list counts as complete only once every entity in it has actually finished —
  // an empty list (e.g. import never attempted) is not complete either.
  private isEntityListComplete(list: SynchronizerHistoryEntityDetails[]): boolean {
    return list.length > 0 && list.every(d => !!d.completedAt);
  }

  isExportComplete(item: SynchronizerHistoryItem): boolean {
    return this.isEntityListComplete(item.export);
  }

  hasExportFailures(item: SynchronizerHistoryItem): boolean {
    return item.export.some(d => d.failureCount > 0);
  }

  hasImportFailures(item: SynchronizerHistoryItem): boolean {
    return item.import.some(d => d.failureCount > 0);
  }

  // Import can only be requested once (importStatus starts null and never goes back), and only
  // once every export item finished cleanly — any failure blocks it entirely.
  canImport(item: SynchronizerHistoryItem): boolean {
    return item.importStatus === null && this.isExportComplete(item) && !this.hasExportFailures(item);
  }

  importStatusLabel(item: SynchronizerHistoryItem): string {
    if (item.importStatus === null) return 'Not started';
    if (item.importStatus === 'in progress') return 'In progress';
    return 'Complete';
  }

  exportItemStatus(detail: SynchronizerHistoryExportEntityDetails): EntityItemStatus {
    if (detail.failureCount > 0) return 'failed';
    if (detail.completedAt) return 'complete';
    return 'in-progress';
  }

  importItemStatus(detail: SynchronizerHistoryImportEntityDetails): EntityItemStatus {
    if (detail.failureCount > 0) return 'failed';
    if (detail.skippedCount === detail.totalCount && detail.successCount === 0) return 'skipped';
    if (detail.completedAt) return 'complete';
    return 'in-progress';
  }

  exportDetailTooltip(detail: SynchronizerHistoryExportEntityDetails): string {
    const parts = [`${detail.totalCount} total`];
    if (detail.failureCount > 0) parts.push(`${detail.failureCount} failed`);
    if (detail.skippedCount > 0) parts.push(`${detail.skippedCount} skipped`);
    parts.push(this.entityDuration(detail));
    return parts.join(', ');
  }

  importDetailTooltip(detail: SynchronizerHistoryImportEntityDetails): string {
    const parts = [`${detail.successCount}/${detail.totalCount} succeeded`];
    if (detail.failureCount > 0) parts.push(`${detail.failureCount} failed`);
    if (detail.skippedCount > 0) parts.push(`${detail.skippedCount} skipped`);
    parts.push(this.entityDuration(detail));
    return parts.join(', ');
  }

  // How long this single entity's own processing took, independent of the phase as a whole.
  private entityDuration(detail: SynchronizerHistoryEntityDetails): string {
    const started = new Date(detail.startedAt);
    const completed = detail.completedAt ? new Date(detail.completedAt) : null;
    return this.duration(started, completed) ?? 'running…';
  }

  // Each entity in export[]/import[] carries its own startedAt/completedAt — the phase as a whole
  // started when the earliest entity started, and (once every entity has finished) finished when
  // the last one completed. Returns null for "finished" while any entity is still running.
  private phaseStartedAt(details: { startedAt: string }[]): Date | null {
    if (details.length === 0) return null;
    return new Date(Math.min(...details.map(d => new Date(d.startedAt).getTime())));
  }

  private phaseCompletedAt(details: { completedAt: string }[]): Date | null {
    if (details.length === 0 || details.some(d => !d.completedAt)) return null;
    return new Date(Math.max(...details.map(d => new Date(d.completedAt).getTime())));
  }

  exportStartedAt(item: SynchronizerHistoryItem): Date | null {
    return this.phaseStartedAt(item.export);
  }

  exportCompletedAt(item: SynchronizerHistoryItem): Date | null {
    return this.phaseCompletedAt(item.export);
  }

  importStartedAt(item: SynchronizerHistoryItem): Date | null {
    return this.phaseStartedAt(item.import);
  }

  importCompletedAt(item: SynchronizerHistoryItem): Date | null {
    return this.phaseCompletedAt(item.import);
  }

  // The batch's overall finish time — the import's completion if it ran, otherwise the export's.
  finishedAt(item: SynchronizerHistoryItem): Date | null {
    return this.importCompletedAt(item) ?? this.exportCompletedAt(item);
  }

  duration(start: Date | null, end: Date | null): string | null {
    if (!start || !end) return null;
    return this.formatDuration(end.getTime() - start.getTime());
  }

  private formatDuration(ms: number): string {
    const totalSeconds = Math.max(0, Math.round(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  }

  requestImport(item: SynchronizerHistoryItem): void {
    if (!this.canImport(item) || this.importingBatchId()) return;

    this.importingBatchId.set(item.batchId);
    this.synchronizerService.syncImport(
      item.organizationId,
      item.sourceEnvironment,
      item.destinationEnvironment as Environments,
      item.securityHashCode,
      item.batchId
    ).subscribe({
      next: (response) => {
        this.importingBatchId.set(null);
        this.result.set({ success: true, details: this.toResultDetails(response) });
        this.loadHistory();
      },
      error: (err) => {
        this.importingBatchId.set(null);
        this.result.set({ success: false, error: err?.error?.message ?? err?.message ?? 'Import request failed.' });
      }
    });
  }

  loadHistory(): void {
    if (!this.organizationId) return;

    this.loadingHistory.set(true);
    this.historyError.set(null);
    this.synchronizerService.syncHistory(this.organizationId, this.currentPage(), this.historyPerPage).subscribe({
      next: (history) => {
        this.historyItems.set(history.items);
        this.totalCount.set(history.totalCount);
        this.loadingHistory.set(false);
      },
      error: (err) => {
        this.historyError.set('Failed to load sync history. | ' + err);
        this.loadingHistory.set(false);
      }
    });
  }

  onPerPageChange(): void {
    this.currentPage.set(1);
    this.loadHistory();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.loadHistory();
  }
}
